import React, { useRef, useState } from 'react';
import type { ViewerFileConfig } from '../../../server/admin/viewer-config/types';
import { authenticatedFetch } from '../../../lib/apiClient';

interface FilesConfigTabProps {
  value: ViewerFileConfig[];
  onChange: (updated: ViewerFileConfig[]) => void;
  companyId?: string;
  productId?: string;
}

function generateId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `f-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

export function FilesConfigTab({ value, onChange, companyId, productId }: FilesConfigTabProps) {
  const fileInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  const addFile = () => {
    onChange([
      ...value,
      { id: generateId(), fileName: '', fileUrl: '', mimeType: 'application/pdf', fileSizeBytes: 0 },
    ]);
  };

  const updateFile = (index: number, field: keyof ViewerFileConfig, val: string | number) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };

  const removeFile = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleFileUpload = async (index: number, file: File) => {
    if (!companyId || !productId) return;
    setUploadingIndex(index);
    try {
      const intentRes = await authenticatedFetch(
        `/api/admin/viewer-projects/${companyId}/${productId}/upload-file`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
            purpose: 'file-attachment',
          }),
        },
      );
      const intentJson = await intentRes.json();
      if (!intentRes.ok) throw new Error(intentJson.error?.message ?? 'Upload failed');

      // Upload to S3
      const uploadRes = await fetch(intentJson.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('File upload to storage failed');

      // Auto-populate fields from upload response
      const updated = [...value];
      updated[index] = {
        ...updated[index],
        fileName: file.name,
        fileUrl: intentJson.data.cdnUrl,
        mimeType: file.type,
        fileSizeBytes: file.size,
      };
      onChange(updated);
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setUploadingIndex(null);
    }
  };

  return (
    <div>
      <div className="vc-list">
        {value.map((file, index) => (
          <div key={file.id} className="vc-list-item">
            <div className="vc-list-item-header">
              <span style={{ flex: 1, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                {file.fileName || `File #${index + 1}`}
                {file.fileSizeBytes > 0 && (
                  <span style={{ marginLeft: '0.5rem', color: 'rgba(255,255,255,0.3)' }}>
                    ({formatFileSize(file.fileSizeBytes)})
                  </span>
                )}
              </span>
              <button
                type="button"
                className="vc-list-item-delete"
                onClick={() => removeFile(index)}
                title="Remove file"
              >
                ✕
              </button>
            </div>
            <div className="vc-list-item-fields">
              <input
                className="vc-input"
                type="text"
                value={file.fileName}
                onChange={(e) => updateFile(index, 'fileName', e.target.value)}
                placeholder="File name (e.g. assembly-manual.pdf)"
              />
              <input
                className="vc-input"
                type="url"
                value={file.fileUrl}
                onChange={(e) => updateFile(index, 'fileUrl', e.target.value)}
                placeholder="File URL (https://...)"
              />
              {companyId && productId && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="vc-add-btn"
                    style={{ margin: 0, fontSize: '0.7rem', padding: '0.3rem 0.6rem' }}
                    onClick={() => fileInputRefs.current.get(index)?.click()}
                    disabled={uploadingIndex === index}
                  >
                    {uploadingIndex === index ? 'Uploading...' : '↑ Upload File'}
                  </button>
                  <input
                    ref={(el) => { if (el) fileInputRefs.current.set(index, el); }}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFileUpload(index, f);
                      e.target.value = '';
                    }}
                  />
                </div>
              )}
              <div className="vc-list-item-row">
                <input
                  className="vc-input"
                  type="text"
                  value={file.mimeType}
                  onChange={(e) => updateFile(index, 'mimeType', e.target.value)}
                  placeholder="MIME type"
                  style={{ flex: 1 }}
                />
                <input
                  className="vc-input"
                  type="number"
                  min={0}
                  value={file.fileSizeBytes}
                  onChange={(e) => updateFile(index, 'fileSizeBytes', parseInt(e.target.value) || 0)}
                  placeholder="Size (bytes)"
                  style={{ width: '7rem' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="button" className="vc-add-btn" onClick={addFile}>
        + Add File
      </button>

      {value.length === 0 && (
        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.75rem', textAlign: 'center' }}>
          Add PDF manuals or other downloadable files for viewers.
        </p>
      )}
    </div>
  );
}
